import React, { useState, useMemo } from 'react';
import { Users, TrendingUp, BarChart3, LineChart, Calendar, Filter, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  LineChart as RechartsLineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const PERFORMANCE_TYPES = ["DYN", "DNF", "STA", "DYNBF", "CNF", "CWT", "FIM", "CWTBF", "16x25m", "16x50m"];
const COLORS = ['#14b8a6', '#3b82f6', '#f59e0b', '#ef4444'];

const PerformanceComparisonTab = ({
  freedivers,
  allPerformances,
  translations,
  t
}) => {
  const [comparisonMode, setComparisonMode] = useState('multi'); // 'multi' or 'single'
  const [selectedFreedivers, setSelectedFreedivers] = useState([]);
  const [selectedFreediverSingle, setSelectedFreediverSingle] = useState(null);
  const [performanceType, setPerformanceType] = useState('DYN');
  const [chartType, setChartType] = useState('line'); // 'line' or 'bar'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Get performances for selected freedivers
  const getFreediverPerformances = (freediverId) => {
    return allPerformances.filter(p => p.freediver_id === freediverId);
  };

  // Filter performances by type and date range
  const filterPerformances = (performances) => {
    return performances.filter(p => {
      const matchesType = p.performance_type === performanceType;
      const perfDate = new Date(p.performance_date);
      const matchesDateFrom = !dateFrom || perfDate >= new Date(dateFrom);
      const matchesDateTo = !dateTo || perfDate <= new Date(dateTo);
      return matchesType && matchesDateFrom && matchesDateTo;
    });
  };

  // Prepare data for multi-freediver comparison (line chart - progress over time)
  const multiFreediverLineData = useMemo(() => {
    if (selectedFreedivers.length === 0) return [];

    const allDates = new Set();
    const freediverData = {};

    selectedFreedivers.forEach(fd => {
      const performances = filterPerformances(getFreediverPerformances(fd.id));
      freediverData[fd.id] = {};
      performances.forEach(p => {
        const date = p.performance_date.split('T')[0];
        allDates.add(date);
        const value = performanceType === 'STA' ? p.duration_seconds : p.distance;
        if (!freediverData[fd.id][date] || value > freediverData[fd.id][date]) {
          freediverData[fd.id][date] = value;
        }
      });
    });

    const sortedDates = Array.from(allDates).sort();
    return sortedDates.map(date => {
      const dataPoint = { date: new Date(date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }) };
      selectedFreedivers.forEach(fd => {
        dataPoint[`${fd.first_name} ${fd.last_name}`] = freediverData[fd.id][date] || null;
      });
      return dataPoint;
    });
  }, [selectedFreedivers, performanceType, dateFrom, dateTo, allPerformances]);

  // Prepare data for multi-freediver comparison (bar chart - best performances)
  const multiFreediverBarData = useMemo(() => {
    if (selectedFreedivers.length === 0) return [];

    return selectedFreedivers.map(fd => {
      const performances = filterPerformances(getFreediverPerformances(fd.id));
      const values = performances.map(p => performanceType === 'STA' ? p.duration_seconds : p.distance);
      const best = values.length > 0 ? Math.max(...values) : 0;
      const average = values.length > 0 ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 0;
      const count = performances.length;
      
      return {
        name: `${fd.first_name} ${fd.last_name}`,
        best,
        average,
        count
      };
    });
  }, [selectedFreedivers, performanceType, dateFrom, dateTo, allPerformances]);

  // Prepare data for single freediver trend analysis
  const singleFreediverTrendData = useMemo(() => {
    if (!selectedFreediverSingle) return [];

    const performances = filterPerformances(getFreediverPerformances(selectedFreediverSingle.id));
    const sortedPerfs = performances.sort((a, b) => new Date(a.performance_date) - new Date(b.performance_date));

    return sortedPerfs.map((p, index) => ({
      date: new Date(p.performance_date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: '2-digit' }),
      value: performanceType === 'STA' ? p.duration_seconds : p.distance,
      location: p.location,
      index: index + 1
    }));
  }, [selectedFreediverSingle, performanceType, dateFrom, dateTo, allPerformances]);

  // Calculate improvement stats for single freediver
  const improvementStats = useMemo(() => {
    if (singleFreediverTrendData.length < 2) return null;

    const first = singleFreediverTrendData[0].value;
    const last = singleFreediverTrendData[singleFreediverTrendData.length - 1].value;
    const best = Math.max(...singleFreediverTrendData.map(d => d.value));
    const average = Math.round(singleFreediverTrendData.reduce((a, b) => a + b.value, 0) / singleFreediverTrendData.length);
    const improvement = last - first;
    const improvementPercent = first > 0 ? ((improvement / first) * 100).toFixed(1) : 0;

    return { first, last, best, average, improvement, improvementPercent, count: singleFreediverTrendData.length };
  }, [singleFreediverTrendData]);

  // Add freediver to comparison
  const addFreediver = (freediverId) => {
    if (selectedFreedivers.length >= 4) return;
    const freediver = freedivers.find(f => f.id === freediverId);
    if (freediver && !selectedFreedivers.find(f => f.id === freediverId)) {
      setSelectedFreedivers([...selectedFreedivers, freediver]);
    }
  };

  // Remove freediver from comparison
  const removeFreediver = (freediverId) => {
    setSelectedFreedivers(selectedFreedivers.filter(f => f.id !== freediverId));
  };

  // Format value based on performance type
  const formatValue = (value) => {
    if (performanceType === 'STA') {
      const mins = Math.floor(value / 60);
      const secs = value % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${value}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-blue-200" data-testid="comparison-title">
          {t?.('performanceComparison') || 'Performance Comparison'}
        </h2>
        <div className="flex items-center space-x-2">
          <Button
            variant={comparisonMode === 'multi' ? 'default' : 'outline'}
            onClick={() => setComparisonMode('multi')}
            className={comparisonMode === 'multi' ? 'bg-teal-600' : 'border-blue-500 text-blue-300'}
            data-testid="multi-comparison-btn"
          >
            <Users className="mr-2" size={16} />
            {t?.('compareFreedivers') || 'Compare Freedivers'}
          </Button>
          <Button
            variant={comparisonMode === 'single' ? 'default' : 'outline'}
            onClick={() => setComparisonMode('single')}
            className={comparisonMode === 'single' ? 'bg-teal-600' : 'border-blue-500 text-blue-300'}
            data-testid="single-trend-btn"
          >
            <TrendingUp className="mr-2" size={16} />
            {t?.('progressTrend') || 'Progress Trend'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-gradient-to-r from-blue-900/40 to-teal-900/40 border-blue-700/30">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            {/* Freediver Selection */}
            <div className="md:col-span-2">
              <Label className="text-blue-200 text-sm">
                {comparisonMode === 'multi' 
                  ? (t?.('selectFreediversMax4') || 'Select Freedivers (max 4)')
                  : (t?.('selectFreediver') || 'Select Freediver')
                }
              </Label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (comparisonMode === 'multi') {
                    addFreediver(value);
                  } else {
                    const fd = freedivers.find(f => f.id === value);
                    setSelectedFreediverSingle(fd);
                  }
                }}
              >
                <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white mt-1" data-testid="freediver-selector">
                  <SelectValue placeholder={t?.('chooseFreediver') || 'Choose freediver...'} />
                </SelectTrigger>
                <SelectContent className="bg-blue-800 border-blue-600">
                  {freedivers.map(fd => (
                    <SelectItem key={fd.id} value={fd.id}>
                      {fd.first_name} {fd.last_name} ({fd.nationality})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Performance Type */}
            <div>
              <Label className="text-blue-200 text-sm">{t?.('performanceType') || 'Performance Type'}</Label>
              <Select value={performanceType} onValueChange={setPerformanceType}>
                <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white mt-1" data-testid="performance-type-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-blue-800 border-blue-600">
                  {PERFORMANCE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date From */}
            <div>
              <Label className="text-blue-200 text-sm">{t?.('fromDate') || 'From Date'}</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-blue-800/50 border-blue-600 text-white mt-1"
              />
            </div>

            {/* Date To */}
            <div>
              <Label className="text-blue-200 text-sm">{t?.('toDate') || 'To Date'}</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-blue-800/50 border-blue-600 text-white mt-1"
              />
            </div>
          </div>

          {/* Selected Freedivers (Multi mode) */}
          {comparisonMode === 'multi' && selectedFreedivers.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedFreedivers.map((fd, index) => (
                <Badge 
                  key={fd.id} 
                  className="px-3 py-1 text-sm"
                  style={{ backgroundColor: COLORS[index], color: 'white' }}
                >
                  {fd.first_name} {fd.last_name}
                  <button
                    onClick={() => removeFreediver(fd.id)}
                    className="ml-2 hover:text-red-200"
                  >
                    <X size={14} />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Selected Freediver (Single mode) */}
          {comparisonMode === 'single' && selectedFreediverSingle && (
            <div className="mt-4">
              <Badge className="px-3 py-1 text-sm bg-teal-600">
                {selectedFreediverSingle.first_name} {selectedFreediverSingle.last_name}
                <button
                  onClick={() => setSelectedFreediverSingle(null)}
                  className="ml-2 hover:text-red-200"
                >
                  <X size={14} />
                </button>
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Multi-Freediver Comparison Mode */}
      {comparisonMode === 'multi' && (
        <>
          {selectedFreedivers.length === 0 ? (
            <Card className="bg-gradient-to-r from-blue-900/30 to-teal-900/30 border-blue-700/30">
              <CardContent className="p-8 text-center">
                <Users className="mx-auto mb-4 text-blue-400" size={48} />
                <h3 className="text-xl font-semibold text-blue-200 mb-2">
                  {t?.('selectFreediversToCompare') || 'Select Freedivers to Compare'}
                </h3>
                <p className="text-blue-300">
                  {t?.('selectUpTo4Freedivers') || 'Select up to 4 freedivers to compare their performances side-by-side.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Chart Type Toggle */}
              <div className="lg:col-span-2 flex justify-center space-x-2">
                <Button
                  variant={chartType === 'line' ? 'default' : 'outline'}
                  onClick={() => setChartType('line')}
                  className={chartType === 'line' ? 'bg-teal-600' : 'border-blue-500 text-blue-300'}
                  size="sm"
                >
                  <LineChart className="mr-2" size={16} />
                  {t?.('progressOverTime') || 'Progress Over Time'}
                </Button>
                <Button
                  variant={chartType === 'bar' ? 'default' : 'outline'}
                  onClick={() => setChartType('bar')}
                  className={chartType === 'bar' ? 'bg-teal-600' : 'border-blue-500 text-blue-300'}
                  size="sm"
                >
                  <BarChart3 className="mr-2" size={16} />
                  {t?.('bestPerformances') || 'Best Performances'}
                </Button>
              </div>

              {/* Line Chart - Progress Over Time */}
              {chartType === 'line' && (
                <Card className="lg:col-span-2 bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30">
                  <CardHeader>
                    <CardTitle className="text-blue-200 flex items-center">
                      <LineChart className="mr-2" size={20} />
                      {t?.('progressOverTime') || 'Progress Over Time'} - {performanceType}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {multiFreediverLineData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={400}>
                        <RechartsLineChart data={multiFreediverLineData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="date" stroke="#9ca3af" />
                          <YAxis stroke="#9ca3af" />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1e3a5f', border: '1px solid #3b82f6', borderRadius: '8px' }}
                            labelStyle={{ color: '#93c5fd' }}
                          />
                          <Legend />
                          {selectedFreedivers.map((fd, index) => (
                            <Line
                              key={fd.id}
                              type="monotone"
                              dataKey={`${fd.first_name} ${fd.last_name}`}
                              stroke={COLORS[index]}
                              strokeWidth={2}
                              dot={{ fill: COLORS[index] }}
                              connectNulls
                            />
                          ))}
                        </RechartsLineChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-blue-300 text-center py-8">
                        {t?.('noDataForSelectedFilters') || 'No data available for the selected filters.'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Bar Chart - Best Performances */}
              {chartType === 'bar' && (
                <Card className="lg:col-span-2 bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30">
                  <CardHeader>
                    <CardTitle className="text-blue-200 flex items-center">
                      <BarChart3 className="mr-2" size={20} />
                      {t?.('bestPerformances') || 'Best Performances'} - {performanceType}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {multiFreediverBarData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={multiFreediverBarData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="name" stroke="#9ca3af" />
                          <YAxis stroke="#9ca3af" />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1e3a5f', border: '1px solid #3b82f6', borderRadius: '8px' }}
                            labelStyle={{ color: '#93c5fd' }}
                            formatter={(value, name) => [formatValue(value), name === 'best' ? (t?.('best') || 'Best') : (t?.('average') || 'Average')]}
                          />
                          <Legend />
                          <Bar dataKey="best" name={t?.('best') || 'Best'} fill="#14b8a6" />
                          <Bar dataKey="average" name={t?.('average') || 'Average'} fill="#3b82f6" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-blue-300 text-center py-8">
                        {t?.('noDataForSelectedFilters') || 'No data available for the selected filters.'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Stats Cards */}
              {multiFreediverBarData.map((data, index) => (
                <Card key={data.name} className="bg-gradient-to-br from-blue-800/30 to-teal-800/30 border-blue-700/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center">
                      <div 
                        className="w-4 h-4 rounded-full mr-2" 
                        style={{ backgroundColor: COLORS[index] }}
                      />
                      <span className="text-blue-200">{data.name}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-teal-300">{formatValue(data.best)}</p>
                        <p className="text-blue-400 text-xs">{t?.('best') || 'Best'}</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-300">{formatValue(data.average)}</p>
                        <p className="text-blue-400 text-xs">{t?.('average') || 'Average'}</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">{data.count}</p>
                        <p className="text-blue-400 text-xs">{t?.('totalPerformances') || 'Performances'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Single Freediver Trend Mode */}
      {comparisonMode === 'single' && (
        <>
          {!selectedFreediverSingle ? (
            <Card className="bg-gradient-to-r from-blue-900/30 to-teal-900/30 border-blue-700/30">
              <CardContent className="p-8 text-center">
                <TrendingUp className="mx-auto mb-4 text-blue-400" size={48} />
                <h3 className="text-xl font-semibold text-blue-200 mb-2">
                  {t?.('selectFreediverForTrend') || 'Select a Freediver'}
                </h3>
                <p className="text-blue-300">
                  {t?.('selectFreediverToAnalyze') || 'Select a freediver to analyze their performance trends over time.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Improvement Stats */}
              {improvementStats && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <Card className="bg-gradient-to-br from-blue-800/40 to-blue-900/40 border-blue-700/30">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-blue-300">{formatValue(improvementStats.first)}</p>
                      <p className="text-blue-400 text-xs">{t?.('firstRecord') || 'First Record'}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-blue-800/40 to-blue-900/40 border-blue-700/30">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-blue-300">{formatValue(improvementStats.last)}</p>
                      <p className="text-blue-400 text-xs">{t?.('latestRecord') || 'Latest Record'}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-teal-800/40 to-teal-900/40 border-teal-700/30">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-teal-300">{formatValue(improvementStats.best)}</p>
                      <p className="text-teal-400 text-xs">{t?.('personalBest') || 'Personal Best'}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-white">{formatValue(improvementStats.average)}</p>
                      <p className="text-blue-400 text-xs">{t?.('average') || 'Average'}</p>
                    </CardContent>
                  </Card>
                  <Card className={`bg-gradient-to-br ${improvementStats.improvement >= 0 ? 'from-green-800/40 to-green-900/40 border-green-700/30' : 'from-red-800/40 to-red-900/40 border-red-700/30'}`}>
                    <CardContent className="p-4 text-center">
                      <p className={`text-2xl font-bold ${improvementStats.improvement >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                        {improvementStats.improvement >= 0 ? '+' : ''}{formatValue(improvementStats.improvement)}
                      </p>
                      <p className={`text-xs ${improvementStats.improvement >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {t?.('improvement') || 'Improvement'}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className={`bg-gradient-to-br ${improvementStats.improvement >= 0 ? 'from-green-800/40 to-green-900/40 border-green-700/30' : 'from-red-800/40 to-red-900/40 border-red-700/30'}`}>
                    <CardContent className="p-4 text-center">
                      <p className={`text-2xl font-bold ${improvementStats.improvement >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                        {improvementStats.improvement >= 0 ? '+' : ''}{improvementStats.improvementPercent}%
                      </p>
                      <p className={`text-xs ${improvementStats.improvement >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {t?.('change') || 'Change'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Trend Chart */}
              <Card className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30">
                <CardHeader>
                  <CardTitle className="text-blue-200 flex items-center">
                    <TrendingUp className="mr-2" size={20} />
                    {t?.('performanceTrend') || 'Performance Trend'} - {performanceType}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {singleFreediverTrendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <RechartsLineChart data={singleFreediverTrendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e3a5f', border: '1px solid #3b82f6', borderRadius: '8px' }}
                          labelStyle={{ color: '#93c5fd' }}
                          formatter={(value) => [formatValue(value), performanceType]}
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="#14b8a6"
                          strokeWidth={3}
                          dot={{ fill: '#14b8a6', strokeWidth: 2, r: 6 }}
                          activeDot={{ r: 8 }}
                        />
                      </RechartsLineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-blue-300 text-center py-8">
                      {t?.('noDataForSelectedFilters') || 'No data available for the selected filters.'}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Performance History Table */}
              {singleFreediverTrendData.length > 0 && (
                <Card className="bg-gradient-to-br from-blue-800/30 to-teal-800/30 border-blue-700/30">
                  <CardHeader>
                    <CardTitle className="text-blue-200">{t?.('performanceHistory') || 'Performance History'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-blue-700/50">
                            <th className="text-left text-blue-300 py-2 px-3">#</th>
                            <th className="text-left text-blue-300 py-2 px-3">{t?.('date') || 'Date'}</th>
                            <th className="text-left text-blue-300 py-2 px-3">{t?.('value') || 'Value'}</th>
                            <th className="text-left text-blue-300 py-2 px-3">{t?.('location') || 'Location'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {singleFreediverTrendData.map((record, index) => (
                            <tr key={index} className="border-b border-blue-800/30 hover:bg-blue-800/20">
                              <td className="py-2 px-3 text-blue-400">{record.index}</td>
                              <td className="py-2 px-3 text-white">{record.date}</td>
                              <td className="py-2 px-3 text-teal-300 font-semibold">{formatValue(record.value)}</td>
                              <td className="py-2 px-3 text-blue-300">{record.location || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PerformanceComparisonTab;
